import { Alert, Card, Table, Td, Th } from '@/components/ui'
import { StagePanel } from '@/components/stage'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num, pct } from '@/sim/parts'

/**
 * Analyse — die Kennwerte, an denen die Rechnung zu messen ist.
 *
 * Die Prüfgrössen stehen oben und nicht im Anhang. Eine Impulsbilanz, die nicht
 * aufgeht, macht jedes Moment darunter wertlos — ohne dass man es den Kurven
 * ansieht, weil ihre Form plausibel bleibt.
 */

export function AnalysisPage() {
  const { simulation } = useSimulation()
  const { dynamics, metrics, balance, gait } = simulation

  const impulseOk = Math.abs(balance.verticalError) < 0.05
  const groundOk = Math.abs(metrics.groundError) < 0.01 && metrics.groundFloat < 0.02

  return (
    <div className="stack">
      <PageHead
        title="Analyse"
      />

      {/* Das Bühnenfenster steht auf jeder Seite, auf der man die Bewegung
          verstellt. Beim Scrollen heftet es sich oben an: der Kopftext wird
          einmal gelesen, die Figur wird immer gebraucht. */}
      <StagePanel />

      <Card title="Prüfgrössen">
        <div className="metrics">
          <Metric
            label="Senkrechter Impuls"
            value={`${num(100 * balance.verticalError, 2)} %`}
            note={`${num(balance.verticalImpulse, 0)} von ${num(balance.verticalExpected, 0)} Ns`}
            warn={!impulseOk}
          />
          <Metric
            label="Waagerechter Impuls"
            value={`${num(balance.horizontalImpulse, 2)} Ns`}
            note="muss über den Zyklus null sein"
            warn={Math.abs(balance.horizontalImpulse) > 0.02 * balance.verticalExpected}
          />
          <Metric
            label="Bodenfehler"
            value={`${num(1000 * metrics.groundError, 1)} mm`}
            note="tiefste Durchdringung"
            warn={Math.abs(metrics.groundError) > 0.01}
          />
          <Metric
            label="Schweben"
            value={`${num(1000 * metrics.groundFloat, 1)} mm`}
            note="belasteter Fuss über dem Boden"
            warn={metrics.groundFloat > 0.02}
          />
          <Metric
            label="Kinematik"
            value={metrics.clipped === 0 ? 'frei' : `${metrics.clipped}× Anschlag`}
            note={`verlangte Beinlänge ${num(100 * gait.report.reach, 1)} %`}
            warn={metrics.clipped > 0}
          />
        </div>

        {!impulseOk && (
          <Alert tone="warning" title="Die Impulsbilanz geht nicht auf">
            Über einen vollen Zyklus muss der senkrechte Bodenkraftimpuls gleich Gewicht
            mal Zyklusdauer sein — der Körper kommt dorthin zurück, wo er war. Fehlt
            etwas, ist irgendwo Kraft verlorengegangen, und alle Momente darunter sind
            zu klein.
          </Alert>
        )}
        {!groundOk && (
          <Alert tone="info" title="Der Fuss steht nicht sauber auf dem Boden">
            Beim gerechneten Gang ist das meist die Beinlänge: die Aufgabe verlangt in
            der Standmitte mehr Bein, als die Maschine hat. Die Nachführung kann ein zu
            kurzes Bein nicht verlängern — was bleibt, ist Schweben, und es gehört
            gezeigt statt weggerechnet.
          </Alert>
        )}
      </Card>

      <div className="metrics">
        <Metric label="Gesamtmasse" value={`${num(metrics.mass, 1)} kg`} />
        <Metric label="Geschwindigkeit" value={`${num(metrics.speed, 2)} m/s`} />
        <Metric label="Doppelschritt" value={`${num(metrics.stride, 2)} m`} />
        <Metric label="Bodenkraft Spitze" value={`${num(metrics.groundPeak, 2)} KG`} />
        <Metric label="Standphase" value={pct(metrics.stance, 0)} />
        <Metric label="Zyklusdauer" value={`${num(metrics.cycleTime, 3)} s`} />
      </div>

      {/*
        HIER STAND EINE ZWEITE MOMENTENTABELLE.

        Gelenk, Ebene, Spitzenmoment, Effektivwert, Leistung — dieselben Zahlen
        aus derselben Quelle wie die Antriebsliste, nur ohne Motor und
        Übersetzung daneben. Zwei Tabellen mit denselben Zahlen sind keine zwei
        Auskünfte: sie sind eine Auskunft und eine Gelegenheit, dass eine von
        beiden stehen bleibt, wenn sich die Rechnung ändert.

        Was diese Seite EINZIGARTIG hat, steht weiter oben und weiter unten: die
        Prüfgrössen des Durchlaufs — geht die Impulsbilanz auf, steht der Fuss
        auf dem Boden — und die Segmenttabelle mit Beschleunigungen und
        Winkelbeschleunigungen. Beides gibt es sonst nirgends.
      */}

      <Card flush title="Segmente">
        <Table>
          <thead>
            <tr>
              <Th>Segment</Th>
              <Th numeric>Masse</Th>
              <Th numeric>Bezugslänge</Th>
              <Th numeric>|a| Spitze</Th>
              <Th numeric>|ω| Spitze</Th>
              <Th numeric>|α| Spitze</Th>
            </tr>
          </thead>
          <tbody>
            {dynamics.segments
              .filter((segment) => segment.side !== 'L')
              .map((segment) => {
                const peak = (values: { x: number; y: number; z: number }[]) =>
                  Math.max(...values.map((v) => Math.hypot(v.x, v.y, v.z)))
                return (
                  <tr key={segment.name}>
                    <Td>{segment.name}</Td>
                    <Td numeric>{num(segment.mass, 2)} kg</Td>
                    <Td numeric>{num(1000 * segment.length, 0)} mm</Td>
                    <Td numeric>{num(peak(segment.acceleration), 1)} m/s²</Td>
                    <Td numeric>{num(peak(segment.omega), 1)} 1/s</Td>
                    <Td numeric>{num(peak(segment.alpha), 0)} 1/s²</Td>
                  </tr>
                )
              })}
          </tbody>
        </Table>
      </Card>
    </div>
  )
}
